/**
 * API client for Requirement Doctor Backend
 * Calls the Go backend under API_BASE_URL, relative to the app's own origin.
 *
 * CONTRACT NOTE: The Backend uses the Kratos default protojson codec. Responses are
 * therefore **camelCase** and proto3 int64 fields are encoded as JSON **strings**
 * (e.g. {"requirementThreadId":"8"}). The authoritative contract is
 * api/openapi.swagger.json in the backend repo. Request bodies may use snake_case
 * (protojson accepts both the proto field name and the json_name on input).
 *
 * TODO: Replace these hand-written types with generated types from the OpenAPI spec.
 */

import { apiCall, API_BASE_URL, type ApiError } from '../../../utils/apiClient';

// ---------------------------------------------------------------------------
// Enums (backend FixAction enum names). The UI works with short ids, mapped here.
// ---------------------------------------------------------------------------

export type FixActionId = 'spell' | 'split' | 'testability' | 'comprehensive';

const FIX_ACTION_ENUM: Record<FixActionId, string> = {
  spell: 'FIX_ACTION_SPELL_CHECK',
  split: 'FIX_ACTION_REQUIREMENT_SPLITTING',
  testability: 'FIX_ACTION_TEST_CONDITIONS',
  comprehensive: 'FIX_ACTION_COMPREHENSIVE',
};

/**
 * Filter arbitrary action id strings down to the valid backend FixActionId set,
 * dropping anything unknown. Falls back to 'comprehensive' when nothing valid
 * remains so the backend (which requires at least one action) never gets an
 * empty or malformed list.
 *
 * Uses hasOwnProperty instead of `in` to avoid matching inherited properties
 * (toString, valueOf, etc.) that exist on every object and would slip through
 * the filter but resolve to a non-string when used as FIX_ACTION_ENUM[id].
 */
export function toFixActionIds(ids: string[]): FixActionId[] {
  const valid = ids.filter((id): id is FixActionId =>
    Object.prototype.hasOwnProperty.call(FIX_ACTION_ENUM, id),
  );
  return valid.length > 0 ? valid : ['comprehensive'];
}

/** Frontend-facing analysis status, normalized from the backend enum name. */
export type JobStatusFe =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Maps a backend AnalysisJobStatus enum name (e.g. "ANALYSIS_JOB_STATUS_RUNNING")
 * to the short UI status. Unknown/unspecified values fall back to "pending".
 */
export function normalizeJobStatus(status: string): JobStatusFe {
  switch (status) {
    case 'ANALYSIS_JOB_STATUS_RUNNING':
      return 'running';
    case 'ANALYSIS_JOB_STATUS_COMPLETED':
      return 'completed';
    case 'ANALYSIS_JOB_STATUS_FAILED':
      return 'failed';
    case 'ANALYSIS_JOB_STATUS_CANCELLED':
      return 'cancelled';
    case 'ANALYSIS_JOB_STATUS_PENDING':
    case 'ANALYSIS_JOB_STATUS_UNSPECIFIED':
    default:
      return 'pending';
  }
}

// ---------------------------------------------------------------------------
// Response types — camelCase, int64 as string, matching the OpenAPI contract.
// ---------------------------------------------------------------------------

export interface RequirementContent {
  text?: { text: string };
}

export interface ImportRequirementResult {
  requirementVersionId: string;
  requirementThreadId: string;
  content: RequirementContent;
  status: string; // REQUIREMENT_VERSION_STATUS_*
}

export interface ImportRequirementsResponse {
  results: ImportRequirementResult[];
}

export interface BrokenRuleDto {
  ruleId: string;
  violation: string;
  fix: string;
  severity: string; // "high" | "medium" | "low" — see normalizeSeverity
}

export interface AnalysisJobDto {
  id: string;
  requirementVersionId: string;
  status: string; // ANALYSIS_JOB_STATUS_*
  progress: number;
  step: string;
  aspiceScore?: number;
  incoseScore?: number;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  violations?: number;
  category?: string;
  brokenRules?: BrokenRuleDto[];
}

export interface AnalyzeRequirementsResponse {
  sessionId: string;
  jobs: AnalysisJobDto[];
}

export interface GetAnalysisSessionResponse {
  sessionId: string;
  jobs: AnalysisJobDto[];
  status: string; // SESSION_STATUS_*
}

// Outcome of one requested fix action. `reason` is a stable code
// ("no_suggestion_of_type" | "conflict_with_applied" | "ambiguous_after_hint" |
// "location_invalid" | "would_break_intent" | "other"); `reasonLabel` is the
// same thing already localised by the backend for the request locale.
export interface FixActionResult {
  action: string; // FIX_ACTION_*
  status: string; // FIX_ACTION_STATUS_*
  reason?: string;
  reasonLabel?: string;
}

// A suggestion apply_changes could not apply (suggestion-based path).
// `title` is what tells the user which suggestion it was: they pick fix types,
// not suggestions, so `suggestionId` means nothing to them.
export interface FixSkippedSuggestion {
  suggestionId: string;
  title?: string;
  reason?: string;
  details?: string;
  reasonLabel?: string;
}

export interface FixRequirementResponse {
  fixId: string;
  beforeText: string;
  afterText: string;
  actionsApplied: string[];
  completedAt: string;
  // Per-action outcome (not rendered), and the suggestions that were skipped.
  // ManualEditor lists `skipped` with its reasons: `notes` only says that
  // nothing was applied, not why.
  actionResults?: FixActionResult[];
  skipped?: FixSkippedSuggestion[];
  // Localised explanation of why before_text == after_text. Empty when the
  // requirement was actually rewritten.
  notes?: string;
}

export interface ApproveThreadResponse {
  requirementThreadId: string;
  approvedVersionId: string;
  status: string;
  threadStatus: string;
  statusLabel: string;
  threadStatusLabel: string;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/**
 * Import requirements from a plain-text string (one requirement per line).
 * Creates DRAFT threads via AddRequirements so they can go through the Doctor
 * analyze → review → fix → approve flow.
 */
export async function importRequirements(
  projectId: number,
  _format: 'text' | 'csv',
  content: string,
): Promise<ImportRequirementsResponse> {
  const lines = content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  return importRequirementTexts(projectId, lines);
}

/**
 * Import an explicit list of requirement texts (already split/parsed by the
 * caller, e.g. from a CSV file). Each text becomes a new DRAFT requirement
 * ready for AI analysis in the Doctor flow.
 */
export async function importRequirementTexts(
  projectId: number,
  texts: string[],
): Promise<ImportRequirementsResponse> {
  const response = await apiCall(`${API_BASE_URL}/requirements/batch`, {
    method: 'POST',
    body: JSON.stringify({
      project_id: projectId,
      requirements: texts.map(text => ({ content: { text: { text } } })),
    }),
  });

  return response.json() as Promise<ImportRequirementsResponse>;
}

/** One AI-suggested merge of two or more redundant requirements. */
export interface MergeProposal {
  sourceThreadIds: string[];
  mergedText: string;
  /** Model confidence in [0.5, 1]. Proposals below 0.5 never reach the client. */
  confidence: number;
  rationale: string;
}

export interface DetectRedundanciesResult {
  /** Ordered by confidence, descending. Empty means "no redundancies found". */
  proposals: MergeProposal[];
  /**
   * The threads actually compared, after the server dropped anything that was
   * not WIP, not in this project, or had no current version.
   */
  analyzedThreadIds: string[];
}

interface DetectRedundanciesResponseDto {
  mergeProposals?: Array<{
    sourceThreadIds?: string[];
    mergedText?: string;
    confidence?: number;
    rationale?: string;
  }>;
  analyzedThreadIds?: string[];
}

/**
 * Detect redundant requirements across a project.
 *
 * `threadIds` scopes the comparison to a selection; omit it to compare every
 * WIP requirement in the project. This is a stateless analysis — nothing is
 * persisted and no merge happens until the user acts on a proposal.
 */
export async function detectRedundancies(
  projectId: number,
  threadIds?: string[],
): Promise<DetectRedundanciesResult> {
  const body: Record<string, unknown> = {};
  // Send the field only when scoping. An empty array means the same thing as
  // omitting it to the backend, but leaving it out keeps the intent explicit.
  if (threadIds && threadIds.length > 0) {
    body.thread_ids = threadIds;
  }
  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/requirements/detect-redundancies`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  const data = (await response.json()) as DetectRedundanciesResponseDto;
  return {
    proposals: (data.mergeProposals ?? []).map(p => ({
      sourceThreadIds: p.sourceThreadIds ?? [],
      mergedText: p.mergedText ?? '',
      confidence: p.confidence ?? 0,
      rationale: p.rationale ?? '',
    })),
    analyzedThreadIds: data.analyzedThreadIds ?? [],
  };
}

/**
 * Start analyzing a batch of requirements by thread id.
 */
export async function analyzeRequirements(
  projectId: number,
  threadIds: string[],
): Promise<AnalyzeRequirementsResponse> {
  const response = await apiCall(`${API_BASE_URL}/requirements/analyze`, {
    method: 'POST',
    body: JSON.stringify({
      project_id: projectId,
      // int64 fields are kept as strings (protojson contract).
      requirement_thread_ids: threadIds,
    }),
  });

  return response.json() as Promise<AnalyzeRequirementsResponse>;
}

/**
 * Poll the status of an analysis session.
 */
export async function getAnalysisSession(
  sessionId: string,
): Promise<GetAnalysisSessionResponse> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/analyze/${sessionId}`,
    {
      method: 'GET',
    },
  );

  return response.json() as Promise<GetAnalysisSessionResponse>;
}

/**
 * Stop an analysis session. Pending and running jobs become CANCELLED and
 * their requirements stay DRAFT; jobs that already finished keep their
 * results. The response has the same shape as getAnalysisSession, so the
 * results that completed before the stop come back without another call.
 */
export async function cancelAnalysisSession(
  sessionId: string,
): Promise<GetAnalysisSessionResponse> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/analyze/${sessionId}/cancel`,
    { method: 'POST', body: JSON.stringify({}) },
  );

  return response.json() as Promise<GetAnalysisSessionResponse>;
}

/**
 * Apply corrective actions to a requirement. UI action ids are mapped to the
 * backend FixAction enum names.
 */
export async function fixRequirement(
  threadId: string,
  versionId: string,
  actions: FixActionId[],
): Promise<FixRequirementResponse> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/fix`,
    {
      method: 'POST',
      body: JSON.stringify({
        requirement_version_id: versionId,
        actions: actions.map(a => FIX_ACTION_ENUM[a]),
      }),
    },
  );

  return response.json() as Promise<FixRequirementResponse>;
}

/**
 * Approve a requirement thread (CB25SES-760 / CB25SES-1055).
 * Transitions the thread from WIP to APPROVED.
 */
export async function approveThread(
  threadId: string,
): Promise<ApproveThreadResponse> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${threadId}/approve`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return response.json() as Promise<ApproveThreadResponse>;
}

/**
 * Reverse a requirement thread approval (APPROVED → WIP) (CB25SES-760).
 */
export async function unapproveThread(threadId: string): Promise<void> {
  await apiCall(`${API_BASE_URL}/requirements/${threadId}/unapprove`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export type { ApiError };
